import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { toggleFetchTableData } from "app/store/patient/slice/patientSlice";
import {
  transmissionTableApi,
  useUpdatePatientSubscriptionMutation,
} from "app/store/transmission/api/transmissionTableApi";
import { changeProfileField } from "app/store/transmission/slice/transmissionSlice";
import { patientApi } from "app/store/patient/api/patientApi";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "static/date";
import { showErrorToast, showToast } from "utils/toast";
import { set } from "lodash";
import {
  isValidDate,
  getFormattedDate,
  getDateDifference,
  getDateJsFromDateString,
  toUTCStartOfDayISOString,
} from "utils/date";
import useDisconnectHistory from "components/pages/transmissions/modal/patient/tabs/subscription/disconnectHistory/useDisconnectHistory";
import EarlierStatuses from "components/pages/transmissions/modal/patient/tabs/subscription/disconnectHistory/EarlierStatuses";

const useSubscribeSettingForm = () => {
  const dispatch = useDispatch();
  const {
    profile,
    deviceInformation,
    patientSerialNumber,
    phoneOptOutInformation,
  } = useSelector((state) => state.transmission);
  const { active: activeDisconnect } = useDisconnectHistory();

  const defaultSetting = {
    subscribe: false,
    next_send: false,
    first_call: false,
    second_call: false,
    third_call: false,
    letter: false,
    disable_configuration: false,
    disabled_type: "",
    disabled_reason: "",
    pause_start_date: null,
    pause_end_date: null,
    notification_channels: {
      email: true,
      call: false,
      sms: false,
      letter: true,
    },
  };

  const [isEditMode, setIsEditMode] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isSubscriptionPaused, setIsSubscriptionPaused] = useState(false);
  const [subscribeSetting, setSubscribeSetting] = useState(defaultSetting);
  const [isPauseActive, setIsPauseActive] = useState(false);

  useEffect(() => {
    const status = profile?.subscribe_setting?.status;
    const isDisableConfiguration = status?.disable_configuration;

    const isRemoved = status?.disabled_type === "Remove";

    const parsedPauseStartDate = status?.pause_start_date
      ? getDateJsFromDateString(status.pause_start_date).format(
          DEFAULT_DATE_FORMAT
        )
      : null;
    const isPaused =
      status?.disabled_type === "Paused" &&
      parsedPauseStartDate === dayjs().format(DEFAULT_DATE_FORMAT);

    setIsPauseActive(isDisableConfiguration && (isRemoved || isPaused));
  }, [
    profile?.subscribe_setting?.status?.disabled_type,
    profile?.subscribe_setting?.status?.pause_start_date,
    profile?.subscribe_setting?.status?.disable_configuration,
  ]);

  // The start date of a pause that has already started cannot be changed.
  // Pause dates are saved as UTC days, so compare UTC days.
  const isPauseStartDateLocked = useMemo(() => {
    const status = profile?.subscribe_setting?.status;

    if (
      !status?.disable_configuration ||
      status?.disabled_type !== "Paused" ||
      !status?.pause_start_date ||
      !status?.pause_end_date
    ) {
      return false;
    }

    const today = getDateJsFromDateString(new Date());

    return (
      !today.isBefore(getDateJsFromDateString(status.pause_start_date)) &&
      !today.isAfter(getDateJsFromDateString(status.pause_end_date))
    );
  }, [profile?.subscribe_setting?.status]);

  const [updatePatientSubscription] = useUpdatePatientSubscriptionMutation();
  useEffect(() => {
    const subscribeStatus = profile?.subscribe_setting?.status;

    if (subscribeStatus?.disabled_type !== "Paused") return;

    const today = dayjs().format(DEFAULT_DATE_FORMAT);
    const isSubscriptionDisabled = subscribeStatus?.disable_configuration;
    const pauseStart = subscribeStatus?.pause_start_date
      ? getDateJsFromDateString(subscribeStatus.pause_start_date).format(
          DEFAULT_DATE_FORMAT
        )
      : null;

    setIsSubscriptionPaused(
      isSubscriptionDisabled && pauseStart && today === pauseStart
    );
  }, [profile?.subscribe_setting]);

  useEffect(() => {
    if (profile?.subscribe_setting) {
      setInitialSubscribeSetting();
    }

    return () => setSubscribeSetting(defaultSetting);
  }, [profile]);

  const lastSendInfoText = useMemo(() => {
    const hasNoDeviceInfo =
      !deviceInformation?.modelName &&
      !deviceInformation?.modelNumber &&
      !(profile?.previous_devices?.length > 0);

    if (profile?.inOfficeOnly || hasNoDeviceInfo) {
      return null;
    }

    const isMonitorDisconnected = Boolean(
      deviceInformation?.is_monitor_disconnected
    );

    const disconnectedDate = isMonitorDisconnected
      ? deviceInformation?.monitor_disconnected_at
      : profile?.late_date;

    const diffInDays =
      disconnectedDate && isValidDate(disconnectedDate)
        ? getDateDifference({
            start: dayjs(disconnectedDate).startOf("day").toDate(),
            end: dayjs().startOf("day").toDate(),
            unit: "days",
            precise: false,
          })
        : 0;

    const lastTransmissionDate =
      profile?.last_send && isValidDate(profile.last_send)
        ? getFormattedDate(profile.last_send, "MM/DD/YYYY hh:mm A")
        : "";

    const hasLastTransmission = Boolean(lastTransmissionDate);
    const hasDisconnectionInfo = isMonitorDisconnected
      ? diffInDays >= 0
      : diffInDays > 0;

    const disconnectedStatus = isMonitorDisconnected
      ? deviceInformation?.monitor_disconnected_status
      : "";
    const statusRecordedAt = activeDisconnect?.latestRecordedAt;
    const statusRecordedDate =
      disconnectedStatus &&
      statusRecordedAt &&
      statusRecordedAt !== activeDisconnect?.disconnectedAt &&
      isValidDate(statusRecordedAt)
        ? getFormattedDate(statusRecordedAt, "MM/DD/YYYY")
        : "";

    // Statuses this disconnect was reported under before the current one.
    const earlierStatuses = isMonitorDisconnected
      ? (activeDisconnect?.statuses || []).slice(0, -1)
      : [];

    if (!hasLastTransmission && !hasDisconnectionInfo && !disconnectedStatus) {
      return null;
    }

    return (
      <span>
        {hasLastTransmission && (
          <>
            Last transmission successfully received on{" "}
            <strong>{lastTransmissionDate}</strong>.
          </>
        )}

        {hasLastTransmission && hasDisconnectionInfo && <br />}

        {hasDisconnectionInfo && (
          <>
            {isMonitorDisconnected ? (
              <>
                The device has been <strong>disconnected</strong>
              </>
            ) : (
              <>
                The scheduled transmission is <strong>late</strong>
              </>
            )}{" "}
            {diffInDays === 0 ? (
              <>today</>
            ) : (
              <>
                for{" "}
                <strong>
                  {diffInDays} {diffInDays === 1 ? "day" : "days"}
                </strong>
              </>
            )}
            .
          </>
        )}

        {disconnectedStatus && (
          <>
            <br />
            Reason: <strong>{disconnectedStatus}</strong>
            {statusRecordedDate ? <> (as of {statusRecordedDate})</> : null}.
          </>
        )}

        {earlierStatuses.length > 0 && (
          <>
            <br />
            <EarlierStatuses statuses={earlierStatuses} />
          </>
        )}
      </span>
    );
  }, [
    profile?.last_send,
    profile?.late_date,
    profile?.inOfficeOnly,
    profile?.previous_devices,
    deviceInformation?.modelName,
    deviceInformation?.modelNumber,
    deviceInformation?.is_monitor_disconnected,
    deviceInformation?.monitor_disconnected_at,
    deviceInformation?.monitor_disconnected_status,
    activeDisconnect,
  ]);

  const optOutInfoText = useMemo(() => {
    if (!phoneOptOutInformation) return "";

    const { primaryNumberOptOut, secondaryNumberOptOut, emergencyPhoneOptOut } =
      phoneOptOutInformation;

    const optOuts = [
      { label: "Primary number", data: primaryNumberOptOut },
      { label: "Secondary number", data: secondaryNumberOptOut },
      { label: "Emergency contact number", data: emergencyPhoneOptOut },
    ];

    const filteredOptOuts = optOuts?.filter(({ data }) => data);

    if (!filteredOptOuts?.length) return "";

    return filteredOptOuts.map(({ label, data }) => (
      <div key={label}>
        {label} <strong>{data.phone}</strong> opted out of notifications on{" "}
        <strong>
          {getFormattedDate(data.optOutDate, "MM/DD/YYYY hh:mm a")}
        </strong>
        .
      </div>
    ));
  }, [phoneOptOutInformation]);

  const setInitialSubscribeSetting = () => {
    setSubscribeSetting({
      ...profile?.subscribe_setting,
      disabled_type: profile?.subscribe_setting?.status?.disabled_type,
      disabled_reason: profile?.subscribe_setting?.status?.disabled_reason,
      disable_configuration:
        profile?.subscribe_setting?.status?.disable_configuration,
      pause_start_date: profile?.subscribe_setting?.status?.pause_start_date
        ? getDateJsFromDateString(
            profile?.subscribe_setting?.status?.pause_start_date
          )
        : null,
      pause_end_date: profile?.subscribe_setting?.status?.pause_end_date
        ? getDateJsFromDateString(
            profile?.subscribe_setting?.status?.pause_end_date
          )
        : null,
      notification_channels: profile?.subscribe_setting
        ?.notification_channels || {
        email: true,
        call: false,
        sms: false,
        letter: true,
      },
    });
  };

  const handleCancel = () => {
    setInitialSubscribeSetting();
    setIsEditMode(false);
  };

  const handleChange = (name, value) => {
    let subscribeSettingPayload = { ...subscribeSetting };

    const todayDate = dayjs().format(DEFAULT_DATE_FORMAT);

    const effectiveDisableConfig =
      name === "disable_configuration"
        ? value
        : subscribeSettingPayload.disable_configuration;

    const isRemoved =
      subscribeSettingPayload.disabled_type === "Remove" ||
      (name === "disabled_type" && value === "Remove");

    const isPaused =
      (subscribeSettingPayload.disabled_type === "Paused" &&
        subscribeSettingPayload.pause_start_date &&
        dayjs(subscribeSettingPayload.pause_start_date).format(
          DEFAULT_DATE_FORMAT
        ) === todayDate) ||
      (name === "pause_start_date" &&
        dayjs(value).format(DEFAULT_DATE_FORMAT) === todayDate);

    const isPauseActiveValue =
      effectiveDisableConfig && (isRemoved || isPaused);

    if (isPauseActiveValue) {
      subscribeSettingPayload = { ...subscribeSetting, subscribe: false };
    }

    setIsPauseActive(isPauseActiveValue);

    let updatedPayload = set({ ...subscribeSettingPayload }, name, value);

    if (name === "disabled_type") {
      updatedPayload.disabled_reason = "";

      if (value === "Remove") {
        updatedPayload.pause_start_date = null;
        updatedPayload.pause_end_date = null;
      } else if (value === "Paused" && isPauseStartDateLocked) {
        updatedPayload.pause_start_date = getDateJsFromDateString(
          profile?.subscribe_setting?.status?.pause_start_date
        );
      }
    }
    setSubscribeSetting(updatedPayload);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setIsSubmitClicked(true);
    try {
      const {
        subscribe,
        disabled_type,
        pause_end_date,
        disabled_reason,
        pause_start_date,
        disable_configuration,
      } = subscribeSetting;

      const isMissingBasicFields = !disabled_type || !disabled_reason;
      const isPausedMissingDates =
        disabled_type === "Paused" && (!pause_start_date || !pause_end_date);
      if (
        disable_configuration &&
        (isMissingBasicFields || isPausedMissingDates)
      ) {
        return;
      }

      const subscribeSettingPayload = {
        ...subscribeSetting,
        pause_start_date: pause_start_date
          ? dayjs(pause_start_date).format(DEFAULT_DATE_FORMAT)
          : null,
        pause_end_date: pause_end_date
          ? dayjs(pause_end_date).format(DEFAULT_DATE_FORMAT)
          : null,
      };

      const { status, connectivity_report, ...updatedSubscribeSettingPayload } =
        subscribeSettingPayload;

      const response = await updatePatientSubscription({
        patientId: profile?.patientId,
        subscribeSetting: updatedSubscribeSettingPayload,
      });

      if (response.error) {
        return showErrorToast(response?.error?.message);
      }

      setIsEditMode(false);
      showToast({ message: response?.data?.message });
      await dispatch(toggleFetchTableData(true));

      const updateSubscribeSettings = {
        ...subscribeSetting,
        status: {
          disable_configuration,
          disabled: disabled_type === "Paused" && pause_start_date,
          disabled_type,
          disabled_reason,
          pause_start_date: toUTCStartOfDayISOString(pause_start_date),
          pause_end_date: toUTCStartOfDayISOString(pause_end_date),
        },
      };

      dispatch(
        changeProfileField({
          type: "profile",
          value: {
            ...profile,
            subscribe_setting: updateSubscribeSettings,
          },
        })
      );

      dispatch(
        transmissionTableApi.util.invalidateTags([
          "getAlertBanner",
          patientSerialNumber,
        ])
      );
      dispatch(patientApi.util.invalidateTags(["subscriptionPauseHistory"]));
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      setIsLoading(false);
      dispatch(toggleFetchTableData(false));
    }
  };

  return {
    profile,
    isSubscriptionPaused,
    subscribeSetting,
    isSubmitClicked,
    isEditMode,
    isLoading,
    optOutInfoText,
    lastSendInfoText,
    isPauseActive,
    isPauseStartDateLocked,
    setIsSubmitClicked,
    handleChange,
    handleCancel,
    handleSubmit,
    setIsEditMode,
  };
};

export default useSubscribeSettingForm;
